type CacheItem<T> =
  | { type: 'pending'; promise: Promise<T>; timestamp: number }
  | { type: 'completed'; result: T; timestamp: number };

export interface CacheOptions {
  maxItems: number;
  // milliseconds
  ttl: number;
}

export class PromiseCache {
  private cache = new Map<string, CacheItem<unknown>>();

  constructor(private options: CacheOptions) {}

  /**
   * Execute operation with caching
   */
  public withCache<T>(key: string, operation: () => Promise<T>): Promise<T> {
    if (this.cache.has(key)) {
      const item = this.cache.get(key) as CacheItem<T>;

      if (this.isExpired(item, this.options.ttl)) {
        this.cache.delete(key);
      } else {
        if (item.type === 'pending') {
          return item.promise;
        }

        return Promise.resolve(item.result);
      }
    }

    let resolveCache: (result: T) => void;

    const promise = operation()
      .then((result) => {
        this.cache.set(key, {
          type: 'completed',
          result,
          timestamp: Date.now(),
        });

        if (resolveCache) resolveCache(result);

        return result;
      })
      .catch((error) => {
        this.cache.delete(key);
        throw error;
      });

    this.cache.set(key, {
      type: 'pending',
      promise,
      timestamp: Date.now(),
    });

    if (this.cache.size > this.options.maxItems) {
      this.cleanup();
    }

    return promise;
  }

  /**
   * Create normalized cache key from object
   */
  public createKey(param: unknown): string {
    const normalized = this.normalizeObject(param);

    return JSON.stringify(normalized);
  }

  private isExpired<T>(item: CacheItem<T>, ttl: number): boolean {
    return item.timestamp + ttl <= Date.now();
  }

  private cleanup(): void {
    const now = Date.now();
    const cutoffTime = now - this.options.ttl;

    for (const [key, item] of this.cache) {
      if (item.timestamp < cutoffTime) {
        this.cache.delete(key);
      }
    }

    if (this.cache.size > this.options.maxItems) {
      const keysToDelete = Array.from(this.cache.keys()).slice(0, this.cache.size - this.options.maxItems);
      keysToDelete.forEach((key) => this.cache.delete(key));
    }
  }

  private normalizeObject(obj: unknown): unknown {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.normalizeObject(item)).filter((item) => !this.isEmpty(item));
    }

    if (obj && typeof obj === 'object' && obj.constructor === Object) {
      const normalized: Record<string, unknown> = {};
      const sortedKeys = Object.keys(obj).sort();

      for (const key of sortedKeys) {
        const value = (obj as Record<string, unknown>)[key];
        if (!this.isEmpty(value)) {
          normalized[key] = this.normalizeObject(value);
        }
      }

      return Object.keys(normalized).length > 0 ? normalized : undefined;
    }

    return obj;
  }

  private isEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === 'object' && Object.keys(value).length === 0) return true;

    return false;
  }
}
